import Text from '@/Components/inputs/Text';
import {
  UsersPaginationFragment,
  UsersQuery,
  type Users_Pagination_Fragment$key,
  type Users_Pagination_Query,
  type Users_Query,
} from '@/graphql/queries/User.query';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { Autocomplete, type AutocompleteProps, type TextFieldProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { type PreloadedQuery, usePreloadedQuery, useQueryLoader, useRelayEnvironment } from 'react-relay';
import Suspense from '../../Suspense';

type UserSelectPaginationData = ReturnType<
  typeof usePaginationFragment<Users_Pagination_Query, Users_Pagination_Fragment$key>
>;

type Option = NonNullable<NonNullable<UserSelectPaginationData['data']>['users']>['edges'][number]['node'];

type ComponentProps = {
  selectProps?: Omit<
    AutocompleteProps<Option, false, false, false>,
    'options' | 'getOptionLabel' | 'renderInput' | 'value'
  >;
  textProps?: Omit<TextFieldProps, 'onChange' | 'value' | 'ref' | 'defaultValue'>;
  queryProps?: {
    defaultVars?: {
      where?: Parameters<UserSelectPaginationData['refetch']>['0']['where'];
    };
  };
};

type SelectComponentProps = {
  isFallback?: boolean;
} & Partial<UserSelectPaginationData> &
  ComponentProps;

const SelectComponent = (props: SelectComponentProps) => {
  const [selectedUserList, setSelectedUserList] = useState<Option | null>(null);

  const isLoading = props?.isFallback || props?.isRefetching;

  const search = (value: string) => {
    if (isLoading) {
      return;
    }

    if (value === '' || !value) {
      props?.refetch?.({ first: 100, where: { ...props?.queryProps?.defaultVars?.where } });
      return;
    }

    props?.refetch?.({
      first: 100,
      where: {
        ...props?.queryProps?.defaultVars?.where,
        search: value,
        searchColumns: ['EMAIL', 'LOGIN', 'NICENAME', 'URL'],
      },
    });
  };

  return (
    <Autocomplete
      id="user-list-select"
      fullWidth
      {...props?.selectProps}
      options={(props?.data?.users?.edges ?? [])
        ?.map((edge) => edge?.node)
        .filter((node) => !!node && !!node?.email?.length)}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option ?? '';
        }
        return [option?.name, option?.email].filter((v) => !!v).join(' - ');
      }}
      value={selectedUserList}
      loading={props?.isFallback}
      multiple={false}
      renderInput={(params) => (
        <Text
          {...params}
          debounceMs={750}
          label="Select a User"
          {...props?.textProps}
          isLoading={isLoading}
          onChange={(e) => {
            search(e.target.value);
          }}
        />
      )}
      onChange={(e, value, reason, details) => {
        props?.selectProps?.onChange?.(e, value, reason, details);

        setSelectedUserList(value as Option);
        if (value === null) {
          props?.refetch?.({ first: 100, where: { ...props?.queryProps?.defaultVars?.where } });
        }
      }}
    />
  );
};

type QueryWrapperProps = {
  queryRef: PreloadedQuery<Users_Query, Record<string, unknown>>;
} & ComponentProps;
const QueryWrapper = (props: QueryWrapperProps) => {
  const environment = useRelayEnvironment();

  const data = usePreloadedQuery<Users_Query>(UsersQuery, props?.queryRef);

  const paginationData = usePaginationFragment<Users_Pagination_Query, Users_Pagination_Fragment$key>(
    UsersPaginationFragment,
    data,
    {
      environment: environment,
    }
  );

  return (
    <SelectComponent
      {...paginationData}
      selectProps={props?.selectProps}
      textProps={props?.textProps}
      queryProps={props?.queryProps}
    />
  );
};

const UserListSelect = (props: ComponentProps) => {
  const [queryRef, loadQuery] = useQueryLoader<Users_Query>(UsersQuery);

  useEffect(() => {
    loadQuery({ first: 100, where: { ...props?.queryProps?.defaultVars?.where } }, { fetchPolicy: 'store-or-network' });
  }, []);

  return (
    <Suspense fallback={<SelectComponent isFallback {...props} />} exists={queryRef}>
      <QueryWrapper queryRef={queryRef!} {...props} />
    </Suspense>
  );
};

export default UserListSelect;
